import React, { ReactElement } from 'react';
import { styled } from '@mui/material/styles';
import { Alert, AlertColor, Box, Link, Snackbar } from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import PersonIcon from '@mui/icons-material/Person';

import { useTranslation } from 'react-i18next';
import Toolbar from '../Toolbar';
import * as userSlice from '../../../data/user.slice';
import authService from '../../../services/authService';
import tableUtils from '../../../services/tableUtils';
import UserDialog from './UserDialog';
import { useCanCreateUser } from '../../../hooks/permissions/useCanCreateUser';
import { useCanEditUser } from '../../../hooks/permissions/useCanEditUser';
import { useCanDeleteUser } from '../../../hooks/permissions/useCanDeleteUser';
import { SortModel } from '../../../data/models/sort-model.model';
import { Provider } from '../../../data/models/provider.model';
import { User } from '../../../data/models/user.model';
import DataTable from '../DataTable';
import { GridCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import RolesDisplay from './RolesDisplay';
import ConfirmationDialog from '../../ConfirmationDialog';
import OnboardingUserDataTable from './OnboardingUserDataTable';
import { FilterOption } from '../../FilterSelect';

const PREFIX = 'Users';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

interface UsersProps {
  provider?: Provider | null;
}

export default function Users({ provider }: UsersProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const canCreateUser = useCanCreateUser();
  const canEditUser = useCanEditUser();
  const canDeleteUser = useCanDeleteUser();

  const selectedFilter = useAppSelector(userSlice.selectSelectedFilter);
  const searchFilter = useAppSelector(userSlice.selectSearchFilter);
  const page = useAppSelector(userSlice.selectPage);
  const pageSize = useAppSelector(userSlice.selectPageSize);
  const sortModel = useAppSelector(userSlice.selectSortModel);
  const loading = useAppSelector(userSlice.selectLoadingUserList);
  const userList = useAppSelector(userSlice.selectUserList);
  const onboardingUsers = useAppSelector(userSlice.selectOnboardingUsers);
  const currentUser = useAppSelector(userSlice.selectUserInfo);
  const userSnackbarMessage = useAppSelector(
    userSlice.selectUserSnackbarMessage,
  );
  const userSnackbarError = useAppSelector(userSlice.selectUserSnackbarError);
  const dispatch = useAppDispatch();

  const [editorOpen, setEditorOpen] = React.useState<boolean>(false);
  const [editorReadOnly, setEditorReadOnly] = React.useState<boolean>(true);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState<string | null>('');
  const [deleteId, setDeleteId] = React.useState(0);
  const [deleteFromOnboardTable, setDeleteFromOnboardTable] =
    React.useState(false);

  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<
    AlertColor | undefined
  >();
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  React.useEffect(() => {
    if (provider?.transportationProviderID) {
      dispatch(
        userSlice.fetchOnboardingUsersAsync(provider.transportationProviderID),
      );
    }
  }, [provider]);

  React.useEffect(() => {
    if (userSnackbarMessage) {
      setSnackbarMessage(userSnackbarMessage);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } else if (userSnackbarError) {
      setSnackbarMessage(userSnackbarError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [userSnackbarMessage, userSnackbarError]);

  React.useEffect(() => {
    if (provider) {
      dispatch(
        userSlice.fetchUserListAsync(
          provider.transportationProviderID!,
          pageSize,
          page,
          selectedFilter,
          searchFilter,
          sortModel,
          true,
        ),
      );
    }
  }, [
    provider,
    selectedFilter,
    searchFilter,
    page,
    pageSize,
    sortModel,
    dispatch,
  ]);

  const [user, setUser] = React.useState<User | null>(null);

  const renderUserLastName = (params: GridCellParams): ReactElement => {
    return (
      <Link
        href="#"
        onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          evt.preventDefault();
          navigate('/users/' + params.row.userAccountID);
        }}
        color="textPrimary"
        underline="always">
        {params?.row?.userLastName}
      </Link>
    );
  };

  const renderRoles = (params: GridCellParams): ReactElement => {
    return <RolesDisplay roles={params?.row?.userAccountRoles} />;
  };

  const onSelectedFilterChange = (val: string) => {
    dispatch(userSlice.setSelectedFilter(val));
  };

  const onSearchChange = (val: string) => {
    dispatch(userSlice.setSearchFilter(val));
  };

  const onPageChange = (pg: number) => {
    dispatch(userSlice.setPage(pg));
  };

  const onPageSizeChange = (pgSize: number) => {
    dispatch(userSlice.setPageSize(pgSize));
  };

  const onSortModelChange = (model: SortModel | null) => {
    dispatch(userSlice.setSortModel(model));
  };

  const addUser = () => {
    dispatch(userSlice.setUser(null));
    setUser(null);
    setEditorReadOnly(false);
    setEditorOpen(true);
  };

  const closeUserEditor = (saved: boolean) => {
    setEditorOpen(false);
    setUser(null);
    dispatch(userSlice.resetSavingFlags());
  };

  const deleteUser = (id: number, email: string, fromOnboardTable = false) => {
    setDeleteId(id);
    setDeleteFromOnboardTable(fromOnboardTable);
    setConfirmMessage(t('users.confirmDelete', { email }));
    setConfirmOpen(true);
  };

  const deleteUserFromOnboardTable = (id: number, email: string) => {
    deleteUser(id, email, true);
  };

  const confirmDeleteUser = () => {
    setConfirmOpen(false);
    dispatch(
      userSlice.deleteUserAsync(
        provider!.transportationProviderID!,
        deleteId,
        !deleteFromOnboardTable,
      ),
    );
  };

  const cancelDeleteUser = () => {
    setConfirmOpen(false);
  };

  const resetPassword = (id: number, email: string) => {
    dispatch(userSlice.resetUserPasswordAsync(id, email));
  };

  const sendUserActivationEmail = (id: number, email: string) => {
    dispatch(userSlice.sendActivationEmailAsync(id, email));
  };

  const closeSnackbar = () => {
    setSnackbarMessage('');
    setSnackbarSeverity(undefined);
    setSnackbarOpen(false);
    dispatch(userSlice.setUserSnackbarMessage(null));
    dispatch(userSlice.setUserSnackbarError(null));
  };

  const columns = tableUtils.getUserColumnDefinition(
    renderUserLastName,
    renderRoles,
    t,
    canDeleteUser ? deleteUser : undefined,
    canEditUser ? resetPassword : undefined,
    currentUser,
  );

  const filterOptions: FilterOption[] = [
    { label: t('providers.filterActive') },
    { label: t('providers.filterActive'), value: 'active0' },
    { label: t('providers.filterNotActive'), value: 'active1' },
  ];

  return (
    <StyledBox className={classes.container}>
      <Toolbar
        button={canCreateUser ? t('users.add') : null}
        importButton={undefined}
        filterOptions={filterOptions}
        currentFilter={selectedFilter}
        searchIcon={<PersonIcon />}
        searchPlaceholder={t('users.search')}
        currentSearchString={searchFilter}
        onFilterChange={onSelectedFilterChange}
        onSearchChange={onSearchChange}
        onCreate={addUser}
        onImport={() => null}
      />
      <DataTable
        getRowId={(row) => row.userAccountID}
        columns={columns}
        itemList={userList}
        pageSize={pageSize}
        sortModel={sortModel}
        loading={loading}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortModelChange={onSortModelChange}
      />
      {!!onboardingUsers?.length && (
        <OnboardingUserDataTable
          users={onboardingUsers}
          onDelete={canDeleteUser ? deleteUserFromOnboardTable : undefined}
          onSendActivationEmail={
            canCreateUser ? sendUserActivationEmail : undefined
          }
          loading={loading}
        />
      )}
      <UserDialog
        open={editorOpen}
        providerId={provider?.transportationProviderID || 0}
        readOnly={editorReadOnly}
        close={closeUserEditor}
      />
      <ConfirmationDialog
        title={t('common.confirmDelete')}
        message={confirmMessage || ''}
        open={confirmOpen}
        positiveAction={confirmDeleteUser}
        negativeAction={cancelDeleteUser}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
}
